import { FormField, FormSection } from "../dtos";
import { compileExpression } from "./payment-plan";

/**
 * §Derive: a field whose value is CALCULATED from sibling fields in the same section (`FormField.derive`) —
 * ported 1:1 from workflow-manager-frontend's lib/derive.ts so the CRM tab computes the same numbers the
 * WM Tasks UI does (e.g. Payment Plan's `Loan Amount = [Cash Price] - [Down Payment (20%)]`).
 *
 * Two spellings are accepted:
 *  - a built-in formula id (legacy: 'downPaymentPct20', 'loanFromCashMinusDp');
 *  - an arithmetic expression whose `[bracketed]` references name a sibling field by stable key, label, or
 *    "<Section>.<Label>", evaluated by the same safe engine as the payment-plan calculator.
 *
 * Missing/empty operand values evaluate as 0, an unresolvable reference or syntax error disables the
 * calculation (manual entry), and cycle members are dropped — `sectionDerivePlan` orders survivors so a
 * chain (A feeds B feeds C) settles in one pass.
 */
export const LEGACY_DERIVE_EXPRESSIONS: Record<string, string> = {
  downPaymentPct20: "ROUND(0.2 * [Cash Price])",
  loanFromCashMinusDp: "[Cash Price] - [Down Payment (20%)]",
};

const deriveExpressionOf = (derive: string): string =>
  LEGACY_DERIVE_EXPRESSIONS[derive] ?? derive;

/** Submission key of a field — MUST match step-form's fieldKeyOf: authored `k` (the engine's
 * lookup key), else placement ref, else "<section>.<label>". */
const keyOf = (section: FormSection, field: FormField): string =>
  field.k ?? field.ref ?? `${section.name}.${field.l}`;

interface DeriveOperand {
  ref: string;
  key: string;
  label: string;
}

interface CompiledDerive {
  operands: DeriveOperand[];
  compute: (data: Record<string, unknown>) => number;
}

const REF_PATTERN = /\[([^\]]+)\]/g;

/** Resolve a `[bracketed]` reference to a CONCRETE sibling: stable key, then label, then "<Section>.<Label>". */
const resolveDeriveRef = (section: FormSection, ref: string): FormField | null => {
  const concrete = section.fields.filter((f) => !f.ref && !f.from);
  return (
    concrete.find((f) => f.k === ref) ??
    concrete.find((f) => f.l === ref) ??
    concrete.find((f) => `${section.name}.${f.l}` === ref) ??
    null
  );
};

/** Compile a field's derive config against its section. Null ⇒ not derivable — manual entry, never a crash. */
const compileDerive = (
  derive: string,
  section: FormSection
): CompiledDerive | null => {
  const expression = deriveExpressionOf(derive);
  const operands: DeriveOperand[] = [];
  const substituted = expression.replace(REF_PATTERN, (_m, ref: string) => {
    const sibling = resolveDeriveRef(section, ref);
    if (!sibling) return "[?]"; // invalid token so compilation fails below
    operands.push({ ref, key: keyOf(section, sibling), label: sibling.l });
    return `_f${operands.length - 1}`;
  });
  try {
    const compiled = compileExpression(substituted);
    if (compiled.variables.some((name) => !/^_f\d+$/.test(name))) return null;
    return {
      operands,
      compute: (data) => {
        const scope: Record<string, number> = {};
        operands.forEach(
          (operand, i) => (scope[`_f${i}`] = Number(data[operand.key] || 0))
        );
        return compiled.evaluate(scope);
      },
    };
  } catch {
    return null;
  }
};

export interface DerivePlanEntry {
  key: string;
  compiled: CompiledDerive;
}

/** The section's derived fields in dependency order; self-references and cycle members are dropped. */
export const sectionDerivePlan = (section: FormSection): DerivePlanEntry[] => {
  const candidates = new Map<string, DerivePlanEntry>();
  for (const field of section.fields) {
    if (typeof field.derive !== "string" || field.ref || field.from) continue;
    const compiled = compileDerive(field.derive, section);
    if (!compiled) continue;
    const key = keyOf(section, field);
    if (compiled.operands.some((o) => o.key === key)) continue; // self-reference
    candidates.set(key, { key, compiled });
  }
  const loopsBack = (start: string): boolean => {
    const stack =
      candidates.get(start)?.compiled.operands.map((o) => o.key) ?? [];
    const seen = new Set<string>();
    while (stack.length) {
      const key = stack.pop() as string;
      if (key === start) return true;
      if (seen.has(key) || !candidates.has(key)) continue;
      seen.add(key);
      stack.push(...(candidates.get(key)?.compiled.operands.map((o) => o.key) ?? []));
    }
    return false;
  };
  const pending = new Map([...candidates].filter(([key]) => !loopsBack(key)));
  const ordered: DerivePlanEntry[] = [];
  let progressed = true;
  while (progressed && pending.size) {
    progressed = false;
    for (const [key, entry] of pending) {
      if (entry.compiled.operands.some((o) => pending.has(o.key))) continue;
      ordered.push(entry);
      pending.delete(key);
      progressed = true;
    }
  }
  return ordered;
};

/**
 * The keys the derive engine is ACTUALLY calculating — workflow-manager's `derivedKeys`
 * (form-renderer.tsx:656), the set its renderer renders read-only. A derive this engine skips
 * (conditional clauses, an unresolvable reference, a cycle) is NOT being calculated, so the field
 * must stay typeable rather than locking a box nothing fills. The presence of a `derive` alone is
 * NOT the test.
 */
export const calculatedKeys = (schema: FormSection[]): Set<string> =>
  new Set(schema.flatMap((section) => sectionDerivePlan(section).map((entry) => entry.key)));

/**
 * Evaluate every section's derive plan against the flat form data, skipping keys the user typed over
 * (`touched`). Returns ONLY the changed values (rounded like the WM UI displays money) so callers can
 * merge minimally.
 */
export const evaluateDerivedFills = (
  schema: FormSection[],
  data: Record<string, unknown>,
  touched: ReadonlySet<string>
): Record<string, number> => {
  const fills: Record<string, number> = {};
  const working = { ...data };
  for (const section of schema) {
    for (const entry of sectionDerivePlan(section)) {
      if (touched.has(entry.key)) continue;
      const value = entry.compiled.compute(working);
      if (!Number.isFinite(value)) continue;
      const rounded = Math.round(value * 100) / 100;
      if (Number(working[entry.key] ?? NaN) !== rounded) {
        fills[entry.key] = rounded;
        working[entry.key] = rounded;
      }
    }
  }
  return fills;
};
